import React, { ChangeEvent, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { ContentState, EditorState, Editor, CompositeDecorator } from 'draft-js';
import { Modal, Box } from '@material-ui/core';
import ModalHeader from './../../Modal/ModalHeader';
import AudioPreviewer from '../AudioPreviewer';
import AcronymForm from '../../Acronym/AcronymForm';
import { AcronymType, AcronymNameType, AcronymTypeObject } from '../../../types/AcronymTypes';
import { updateAcronym } from '../../../apis/acronym';
import getFragmentFromSelection from 'draft-js/lib/getFragmentFromSelection';
import { DocumentType, ImagesOfDocument } from '../../../types/AppTypes';
import './SimpleEditor.css';
import { faDownload, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DocumentStatusType, documentConfig } from '../../../config';
import CustomButton from '../../../components/ui/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Image, Form, ModalBody, ModalFooter, Alert, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getExtensionOfFile } from '../../../utils/utils';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import HtmlAudioPlayer from '../HtmlAudioPlayer';

const findWithRegex = (regex: RegExp, contentBlock: any, callback: (start: number, end: number) => void) => {
  const text = contentBlock.getText();
  let matchArr, start, end;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    end = start + matchArr[0].length;
    callback(start, end);
  }
};

const SearchHighlight = (props: any): JSX.Element => <span className="search-and-replace-highlight">{props.children}</span>;

const generateDecorator = (highlightTerm: string) => {
  let regex: RegExp;
  try {
    regex = new RegExp(highlightTerm, 'g');
  } catch (_) {
    highlightTerm = '';
  }
  return new CompositeDecorator([
    {
      strategy: (contentBlock, callback) => {
        if (highlightTerm !== '') {
          findWithRegex(regex, contentBlock, callback);
        }
      },
      component: SearchHighlight,
    },
  ]);
};

type Props = {
  initialText?: string;
  document: DocumentType;
  mp3Url: string | undefined;
  onChange: (value: string) => void;
  onDownloadAudio: (value: string) => void;
  onPublish: () => void;
  handleImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleImageRemove: (index: number, imageId: any) => void;
  images: ImagesOfDocument[] | undefined;
  handleChangeTitle: (event: ChangeEvent<HTMLInputElement>) => void;
  validTitle: boolean;
  title?: string;
  docId: string;
};

const SimpleEditor = (props: Props): JSX.Element => {
  let editor: Editor | null = null;
  const contentState = ContentState.createFromText(props.initialText || '');
  let initEditorState = EditorState.createWithContent(contentState);
  initEditorState = EditorState.moveFocusToEnd(initEditorState);

  const [search, setSearch] = useState('');
  const [replace, setReplace] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [editorState, setEditorState] = useState<EditorState>(initEditorState);
  const [open, setOpen] = useState<boolean>(false);
  const acronymObject = new AcronymTypeObject();
  acronymObject.documentId = props.document.documentId;
  acronymObject.type = AcronymNameType.Jargon;
  const [acronym, setAcronym] = useState<AcronymType>(acronymObject);
  const [showAlert, setShowAlert] = useState(false);
  const [alertVariant, setAlertVariant] = useState('success');
  const [message, setMessage] = useState('');
  const [eligibleConvertionAlert, setEligibleConvertionAlert] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [openPhotoModal, setOpenPhotoModal] = useState(false);
  const [photo, setPhoto] = useState('');
  const [imageNumbers, setImageNumbers] = useState<number[]>([]);
  const [titleEditable, setTitleEditable] = useState(true);

  const inputRef = useRef<any>(null);

  const onChangeSearch = (e: SyntheticEvent) => {
    const newSearch = (e.target as HTMLInputElement).value;
    setSearch(newSearch);
    const newEditorState = EditorState.set(editorState, {
      decorator: generateDecorator(newSearch),
    });
    setEditorState(newEditorState);
  };

  const onChangeReplace = (e: SyntheticEvent) => {
    setReplace((e.target as HTMLInputElement).value);
  };

  const onReplace = () => {
    const regex = new RegExp(search, 'g');
    let contentState = editorState.getCurrentContent();

    const text = contentState.getPlainText();
    const newText = text.replaceAll(regex, replace);
    const newContentState = ContentState.createFromText(newText);

    let newEditorState = EditorState.createWithContent(newContentState);
    newEditorState = EditorState.moveFocusToEnd(newEditorState);

    setEditorState(newEditorState);
    props.onChange(newEditorState.getCurrentContent().getPlainText());
  };

  const onChange = (editorState: EditorState) => {
    let selectedText = '';
    if (editorState.getSelection().getHasFocus()) {
      const selected = getFragmentFromSelection(editorState);
      selectedText = selected ? selected.map((x: any) => x.getText()).join('\n') : '';
      setEditorState(editorState);
      setSelectedText(selectedText);
      setAcronym({
        ...acronym,
        name: selectedText,
        pronounce: selectedText,
      });
      props.onChange(editorState.getCurrentContent().getPlainText());
    } else {
      setEditorState(editorState);
    }
  };

  const onAddAcronym = () => {
    console.log(selectedText);
    setOpen(true);
  };

  const updateAcronymRow = async (acr: AcronymType) => {
    setOpen(false);

    const { data, errors } = await updateAcronym(acronym.name, {
      ...acronym,
      ...acr,
    });
    console.log(data, errors);
    if (errors) {
      setShowAlert(true);
      setAlertVariant('danger');
      setMessage(`Error adding ${acronym.name}`);
    } else {
      setShowAlert(true);
      setAlertVariant('success');
      setMessage(`${acronym.name} added`);
    }
  };

  const updateName = (e: SyntheticEvent) => {
    const name = (e.target as HTMLInputElement).value;
    setAcronym({
      ...acronym,
      name,
    });
  };

  const updatePronounce = (e: SyntheticEvent) => {
    const pronounce = (e.target as HTMLInputElement).value;
    setAcronym({
      ...acronym,
      pronounce,
    });
  };

  const convertToLocalDate = (date: string) => {
    const dateString = new Date(date).toLocaleString('en-US');
    return dateString;
  };

  const checkSelectedTextLength = () => {
    console.log('length', selectedText.length);
    if (selectedText.trim().length <= 3000) {
      setEligibleConvertionAlert(false);
    } else {
      setEligibleConvertionAlert(true);
    }
  };

  // OSEXIQC-272 next-previous buttons
  // when document change reinitialize editor with new text
  useEffect(() => {
    const contentState = ContentState.createFromText(props.initialText || '');
    let initEditorState = EditorState.createWithContent(contentState);
    initEditorState = EditorState.moveFocusToEnd(initEditorState);
    setEditorState(initEditorState);
  }, [props.docId]);

  useEffect(() => {
    if (!titleEditable) {
      inputRef?.current?.focus();
    }
  }, [titleEditable]);

  const DocumentEditorHeader = () => {
    return (
      <>
        <div className="document-header-title">
          {props?.document?.state === 'IN_REVIEW' ||
          props?.document?.state === 'READY' ||
          props?.document?.state === 'REVIEWING' ? (
            <Button className="mr-3" variant="primary" onClick={props?.onPublish}>
              Publish
            </Button>
          ) : (
            ''
          )}
          <h1 style={{ flex: '0 0 100%' }}>
            {props?.document?.documentId} ({documentConfig.status[props?.document?.state as keyof DocumentStatusType] || '?'}
            )
          </h1>
        </div>
        <div style={{ display: 'flex', maxWidth: '100%', flex: '0 0 100%' }}>
          <Row style={{ width: '50%' }}>
            <Col>
              <Form.Group className="mb-3 mt-2" controlId="titleId">
                <div className="d-flex align-items-center">
                  <Form.Label className="mr-2 mb-0" style={{ fontWeight: 600 }}>
                    Title
                  </Form.Label>
                  <Form.Control
                    ref={inputRef}
                    as="input"
                    value={props?.title}
                    onChange={props?.handleChangeTitle}
                    disabled={titleEditable}
                  />
                  <FontAwesomeIcon
                    icon={faEdit}
                    style={{ marginLeft: '5px' }}
                    onClick={() => setTitleEditable(!titleEditable)}
                  />
                </div>
                <Form.Control.Feedback
                  type="invalid"
                  style={{
                    display: `${props?.validTitle ? 'none' : 'block'}`,
                  }}
                >
                  Please insert title.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>
        <div className="document-header-player">
          {props?.mp3Url && props?.document?.state !== 'PENDING' ? (
            <div className="audioPlayerContainer">
              {/* <ReactPlayer url={mp3Url} controls width="100%" height="30px" /> */}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h5 style={{ marginTop: '10px', opacity: '.8' }}>
                  Version {props?.document.version}, {convertToLocalDate(props?.document.audioCreated || '')}
                </h5>
                <CustomButton type="transparent" onClick={() => props?.onDownloadAudio(props.mp3Url!!)}>
                  <span style={{ marginRight: '5px' }}>Download audio</span>
                  <FontAwesomeIcon icon={faDownload} />
                </CustomButton>
              </div>

              {/* <ReactJWPlayer playerId="jwPlayer" playerScript="https://cdn.jwplayer.com/libraries/bxrnssnn.js" file={props?.mp3Url} />   */}

              <HtmlAudioPlayer mp3File={props.mp3Url} />
            </div>
          ) : (
            ''
          )}
        </div>
      </>
    );
  };

  const updateFollowUp = (e: SyntheticEvent) => {
    const followUp = (e.target as HTMLInputElement).checked;
    setAcronym({
      ...acronym,
      followUp,
    });
  };

  const onHoverPhoto = (src) => {
    setOpenPhotoModal(true);
    setPhoto(src);
  };

  const handleClosePhotoModal = () => {
    setOpenPhotoModal(false);
  };

  const ImageWithZoomEffect = ({ index, src, extension }) => {
    if (src !== undefined && extension !== null) {
      return (
        <img
          key={`image-${index}`}
          src={imageUrls?.filter((url) => url?.includes(`image${index}.${extension}`))[0]}
          alt={`Image ${index}`}
          style={{ width: '10%' }}
          onMouseEnter={() => onHoverPhoto(imageUrls?.filter((url) => url?.includes(`image${index}.${extension}`))[0])}
          onMouseLeave={handleClosePhotoModal}
        />
      );
    } else {
      return <span style={{ color: 'red', fontWeight: 500 }}>{`{image${index}} not found`}</span>;
    }
  };

  const text = props?.document?.text;
  const imageUrls = props?.images?.map((detail) => detail.url) ?? [];
  let source: any = '';
  let ext: any = '';
  const replacedTextArray = text?.split(/\{image\d+\}/g).map((segment, index) => (
    <React.Fragment key={index}>
      {segment}
      {index < imageNumbers.length &&
        ((source = imageUrls?.filter(
          (url) =>
            url?.includes(`image${imageNumbers[index]}.png`) ||
            url?.includes(`image${imageNumbers[index]}.jpg`) ||
            url?.includes(`image${imageNumbers[index]}.jpeg`),
        )[0]),
        (ext = getExtensionOfFile(source)),
        (<ImageWithZoomEffect index={imageNumbers[index]} src={source} extension={ext} />))}
    </React.Fragment>
  ));

  const onPreview = () => {
    setOpenPreviewModal(true);
  };

  const handleClosePreviewModal = () => {
    setOpenPreviewModal(false);
  };

  useEffect(() => {
    if (selectedText != '') {
      checkSelectedTextLength();
    }
  }, [selectedText]);

  useEffect(() => {
    const regex = /\bimage(\d+)\b/g;
    const matches = Array.from(props?.document?.text?.matchAll(regex) ?? []);
    setImageNumbers(matches.map((match) => parseInt(match[1], 10)));
  }, [props?.document?.text]);

  return (
    <div>
      {showAlert ? (
        <div style={{ position: 'absolute', top: 30, right: 0 }}>
          <Alert variant={alertVariant} dismissible onClose={() => setShowAlert(false)}>
            {message}
          </Alert>
        </div>
      ) : (
        ''
      )}
      <div className="editor-header-sticky">
        {DocumentEditorHeader()}
        <div className="editor-top-sticky">
          <div className="search-and-replace">
            <input value={search} onChange={onChangeSearch} placeholder="Search..." />
            <input value={replace} onChange={onChangeReplace} placeholder="Replace..." />
            <button onClick={onReplace}>Replace</button>
          </div>
          <div className="editor-toolbar">
            <div>
              <AudioPreviewer text={selectedText} document={props.document} autoplay={false} id="audio-player-1" />
            </div>
            {selectedText ? (
              <div>
                <Button variant="primary" onClick={onAddAcronym}>
                  Add Acronym
                </Button>
              </div>
            ) : (
              ''
            )}
          </div>
          {eligibleConvertionAlert && (
            <div style={{ position: 'absolute', top: 60, right: 0 }}>
              <Alert
                style={{ position: 'relative' }}
                variant="warning"
                dismissible
                onClose={() => setEligibleConvertionAlert(false)}
              >
                Selected text is greater than 3000 characters.
              </Alert>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', marginLeft: 20 }}>
          <Form.Group controlId="formFile" style={{ flex: 3 }}>
            <Form.Label>Select images</Form.Label>
            <Form.Control type="file" accept=".png, .jpg, .jpeg" onChange={props?.handleImageChange} multiple />
          </Form.Group>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', marginLeft: 20 }}>
          <Button variant="primary" onClick={onPreview}>
            Preview
          </Button>
        </div>
      </div>
      <div
        style={{ display: 'flex' }}
        className={`${
          props?.document?.isDocFromMp3 || props?.document?.externalUrl ? 'editor--readonly' : 'editor--standard'
        }`}
      >
        <div style={{ flex: props.images!?.length > 0 ? 9 : 12, marginRight: '10px' }}>
          <Editor
            ref={(elem: Editor) => {
              // eslint-disable-next-line
              editor = elem;
            }}
            editorState={editorState}
            onChange={onChange}
            readOnly={props?.document?.isDocFromMp3 || props?.document?.externalUrl != undefined}
          />
        </div>
        {props!.images!.length > 0 ? (
          <div style={{ flex: 3, minHeight: '300px', maxHeight: '700px', overflow: 'scroll' }}>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {props.images?.map((image, index) => (
                <li key={index} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ flex: 10 }}>
                      <Image src={image.url} alt={`Image ${index + 1}`} rounded fluid />
                    </div>
                    <div style={{ flex: 2, textAlign: 'center' }}>
                      <FontAwesomeIcon
                        title="Delete Image"
                        style={{ marginLeft: 5, marginTop: 5, color: 'red', cursor: 'pointer' }}
                        icon={faTrash}
                        onClick={() => props?.handleImageRemove(index, image?.id)}
                      />
                    </div>
                  </div>
                  <label style={{ display: 'block', marginTop: '5px' }}>{image.name}</label>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <></>
        )}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="modal-wrapper">
          {open && (
            <>
              <ModalHeader title={acronym?.name} onClose={() => setOpen(false)} />
              <ModalBody>
                <AcronymForm
                  acronym={acronym}
                  updateAcronymRow={updateAcronymRow}
                  updateName={updateName}
                  updatePronounce={updatePronounce}
                  updateFollowUp={updateFollowUp}
                  allowAddToDict={false}
                  allowSkip={false}
                  canEditField={true}
                />
              </ModalBody>
            </>
          )}
        </Box>
      </Modal>
      <Modal
        open={openPreviewModal}
        onClose={handleClosePreviewModal}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <Box className="modal-wrapper" style={{ width: '95%', height: '90%' }}>
          <Box className="modal-header">
            <Box className="title">Preview Text</Box>
          </Box>
          <ModalBody style={{ height: '80%', maxHeight: '80%', overflow: 'scroll' }}>{replacedTextArray}</ModalBody>
          <ModalFooter>
            <Button variant="danger" onClick={handleClosePreviewModal}>
              Close
            </Button>
          </ModalFooter>
        </Box>
      </Modal>
      <Modal
        open={openPhotoModal}
        onClose={() => setOpenPhotoModal(false)}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <Box className="modal-wrapper">
          <ModalBody style={{ maxHeight: '80%', overflow: 'scroll' }}>
            <Image src={photo} style={{ width: '100%' }} />
          </ModalBody>
        </Box>
      </Modal>
    </div>
  );
};

export default SimpleEditor;
